import {
    AccountMappingTemplateGroupItem,
    AccountType,
    AnnualReportBusinessModel,
    IAccount,
    IAccountMappingSubFLI,
    IAccountMappingTemplate,
    IAccountMappingTemplateGroup,
    IAccountMappingTemplateGroupItemFLI,
    ItemType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { IAccountMappingMap } from '@modeso/types__aumico-lib-annualreport-be/dist/models/interfaces/IAccountMappingMap.interface';
import { EMPTY_STRING } from '../../../../aumico-lib-settings-fe/src/lib/constants/constants';
import { germanySmeAccountPlan } from '../constants/germany-sme-account-plan';
import { smeAccountPlan, smeAccountPlanExactNumbers } from '../constants/sme-account-plan';

// --- REV-18627 (C1/B7): die Rückgabeform des Helpers. ---------------------------------------------

/** Zuordnungsformat der importierten Vorlage (seams §5.1: String-Union, kein Enum). */
export type AssignmentsFormat = 'accountNumber' | 'purpose';

export type MappingWarningKind =
    | 'FORMAT_MISMATCH'
    | 'UNKNOWN_PURPOSE'
    | 'PURPOSE_SHAPED_VALUE_IN_NUMBER_PATH'
    | 'NO_CANDIDATE'
    /** Review R4: `accountAssignmentsFormat` der Vorlage ist weder `accountNumber` noch `purpose`. */
    | 'UNKNOWN_FORMAT';

/** `key` trägt nur Kontonummer bzw. Zweck-Schlüssel — nie Kontoname, nie Saldo (seams N4). */
export interface MappingWarning {
    kind: MappingWarningKind;
    key: string;
}

// REV-18627(sec F4/N4): der Schlüssel einer Warnung darf keinen Rohwert unbeschränkter Länge aus
// der Datei tragen (N4) — die Mengenbegrenzung je Warnart gehört in die Anzeige, nicht hierher
// (sec-fix2: das Ergebnisobjekt ist eine Datenstruktur für Aufrufer, kein Anzeigetext).
const MAX_WARNING_KEY_LENGTH = 64;

function truncateWarningKey(key: string): string {
    if (key.length <= MAX_WARNING_KEY_LENGTH) {
        return key;
    }
    return `${key.slice(0, MAX_WARNING_KEY_LENGTH - 1)}…`;
}

function truncateWarningKeys(warnings: MappingWarning[]): MappingWarning[] {
    return warnings.map((warning) => ({ kind: warning.kind, key: truncateWarningKey(warning.key) }));
}

export interface AccountMappingResult {
    /** Welcher Codepfad gelaufen ist. */
    strategy: AssignmentsFormat | 'sme';
    /** Kontonummern, die keinen Posten gefunden haben — inklusive der heute verschluckten Würfe. */
    unmapped: string[];
    warnings: MappingWarning[];
}

export interface AccountMappingOptions {
    /** E6: die Datei dieser Sitzung trug eine Postenstruktur ⇒ bestehende Zuordnung ersetzen. */
    replaceExistingMapping?: boolean;
    /**
     * REV-22122: Postenname → DATEV-Saldoart ("Aktiva"/"Passiva"/"Aufwand"/"Ertrag") der importierten
     * Vorlage (`accountItemSides`, datev.excel.helper.ts). Ohne sie gilt der `fliType` des Postens —
     * der unterscheidet in der Erfolgsrechnung Ertrag und Aufwand nicht.
     */
    itemSides?: Record<string, string>;
}

export class AccountMappingFormHelper {
    static mapSmeAccountsToFlisAndSubFlis(
        smeMappingItems: (IAccountMappingTemplateGroupItemFLI | IAccountMappingSubFLI)[],
        accounts: IAccount[],
        originallyMappedAccountNumbers: Set<string>,
        businessModel: AnnualReportBusinessModel,
        imporedAccountMappingPlan?: IAccountMappingMap,
        assignmentsFormat?: AssignmentsFormat,
        options?: AccountMappingOptions,
    ): AccountMappingResult | void {
        //CHeck if we need to do the static way:
        if (imporedAccountMappingPlan) {
            return AccountMappingFormHelper.mapWithImportedPlan(
                smeMappingItems,
                accounts,
                originallyMappedAccountNumbers,
                imporedAccountMappingPlan,
                assignmentsFormat,
                options,
            );
        } else {
            const substringCharacterCount = businessModel === AnnualReportBusinessModel.HGB ? 4 : 3;
            // A Map that has all unmappedAccounts which are eligible for sme mapping
            const accountNumberSmeSearchTermToAccounts: Map<string, IAccount[]> =
                AccountMappingFormHelper.getAccountNumberSmeSearchTermToAccounts(
                    accounts,
                    originallyMappedAccountNumbers,
                    substringCharacterCount,
                );
            const smeAccountPlan = AccountMappingFormHelper.getSmeAccountPlan(businessModel);
            smeMappingItems.forEach((item) => {
                const itemName = this.trimSubstringFromEnd(item.name, ' (DE)');
                // an array of strings that has the first 3/4 digits of all acounts that belong to fli in sme plan
                const smeAccountNumbers =
                    smeAccountPlan[itemName] != null
                        ? smeAccountPlan[itemName].map((number) => number.substring(0, substringCharacterCount))
                        : [];
                // when taking the first 3/4 digits from the sme account plan, some numbers will be duplicated
                // so we need to filter the smeAccountNumbers and only keep the unique entries
                const smeAccountNumbersSet = new Set(smeAccountNumbers);
                const filteredSmeAccountNumbers = Array.from(smeAccountNumbersSet);
                filteredSmeAccountNumbers.forEach((smeAccountNumber: string) => {
                    let accountsToMap: IAccount[] = [];
                    if (smeAccountNumber.length === substringCharacterCount) {
                        accountsToMap = accountNumberSmeSearchTermToAccounts.has(smeAccountNumber)
                            ? accountNumberSmeSearchTermToAccounts.get(smeAccountNumber)
                            : [];
                    } else {
                        for (const key of accountNumberSmeSearchTermToAccounts.keys()) {
                            if (key.startsWith(smeAccountNumber)) {
                                accountsToMap.push(...accountNumberSmeSearchTermToAccounts.get(key));
                            }
                        }
                    }
                    item.accounts.push(...accountsToMap);
                    item.accounts.sort((a, b) => a.type.localeCompare(b.type));
                });
                // Exact account numbers (`smeAccountPlanExactNumbers`, CH plan only): full-number match.
                const exactNumbers =
                    businessModel === AnnualReportBusinessModel.HGB ? [] : (smeAccountPlanExactNumbers[itemName] ?? []);
                exactNumbers.forEach((exactNumber) => {
                    const candidates =
                        accountNumberSmeSearchTermToAccounts.get(exactNumber.substring(0, substringCharacterCount)) ?? [];
                    const exactAccounts = candidates.filter(
                        (account) => account.accountNumber === exactNumber && !item.accounts.includes(account),
                    );
                    if (exactAccounts.length === 0) return;
                    item.accounts.push(...exactAccounts);
                    item.accounts.sort((a, b) => a.type.localeCompare(b.type));
                });
            });
        }
    }

    /**
     * REV-18627 (B1/B4/B7): ein Codepfad für Kontonummer- und Zweck-Zuordnung. Baut das Regelwerk
     * je nach Format (Intervall vs. Gleichheit über refNr), wendet den Seitenfilter als Mengenfilter
     * mit E8-Rückfall an und meldet jeden Fall, statt ihn stumm zu verlieren.
     */
    private static mapWithImportedPlan(
        smeMappingItems: (IAccountMappingTemplateGroupItemFLI | IAccountMappingSubFLI)[],
        accounts: IAccount[],
        originallyMappedAccountNumbers: Set<string>,
        imporedAccountMappingPlan: IAccountMappingMap,
        assignmentsFormat: AssignmentsFormat | undefined,
        options: AccountMappingOptions | undefined,
    ): AccountMappingResult {
        const warnings: MappingWarning[] = [];
        const unmapped: string[] = [];
        const smeItemMap = new Map<string, IAccountMappingTemplateGroupItemFLI | IAccountMappingSubFLI>();
        smeMappingItems.forEach((item) => smeItemMap.set(item.name, item));

        let mismatch = false;
        let strategy: AssignmentsFormat = 'accountNumber';
        if (assignmentsFormat === 'purpose') {
            const anyAccountHasPurpose = accounts.some((account) => account.purpose != null);
            if (anyAccountHasPurpose) {
                strategy = 'purpose';
            } else {
                // E4/B7: Vorlage sagt "purpose", die Konten kennen keinen Zweck — Formatkonflikt.
                mismatch = true;
                warnings.push({ kind: 'FORMAT_MISMATCH', key: 'purpose' });
            }
        } else if (assignmentsFormat != null && assignmentsFormat !== 'accountNumber') {
            // Review R4: ein Fremdwert (z. B. aus einem direkten PUT /api/annualreport, serverseitig
            // gibt es keine Whitelist) lief bisher still als Nummern-Pfad. Der Rückfall bleibt, aber
            // er wird gemeldet — der Schlüssel ist der Fremdwert selbst (gekappt wie jeder Schlüssel).
            warnings.push({ kind: 'UNKNOWN_FORMAT', key: String(assignmentsFormat) });
        }

        const intervalRules =
            strategy === 'accountNumber' ? buildIntervalRules(imporedAccountMappingPlan, warnings) : undefined;
        const equalityRules = strategy === 'purpose' ? buildEqualityRules(imporedAccountMappingPlan) : undefined;

        const candidatesFor = (account: IAccount): Map<string, Set<Sign>> =>
            strategy === 'purpose'
                ? candidatesForPurposeStrategy(account, equalityRules!)
                : candidatesForAccountNumberStrategy(account, intervalRules!);

        // Bei Formatkonflikt wird NIE ersetzt (B7). Im Erstjahr gibt es nichts zu ersetzen
        // (`originallyMappedAccountNumbers` leer) — das Signal bleibt dort wirkungslos, sonst
        // fallen Konten ohne Kandidaten aus dem Erstjahr-Import lautlos aus `unmapped` (REV-18627 C1e).
        const effectiveReplace =
            !!options?.replaceExistingMapping && !mismatch && originallyMappedAccountNumbers.size > 0;

        const sideOf = sideOfItems(smeMappingItems, options?.itemSides);
        // REV-22122 (review E1): a DATEV income statement carries income positive and expense negative,
        // so Soll is the NEGATIVE balance there. Only for DATEV plans — an aumico-format plan carries no
        // format, and its income-statement signs follow the customer's import settings.
        const datevIncomeSides = assignmentsFormat === 'accountNumber' || assignmentsFormat === 'purpose';
        const nettingGroups = new Map<string, NettingGroup>();

        const mapAccount = (account: IAccount): void => {
            const candidates = candidatesFor(account);
            if (candidates.size === 0) {
                if (strategy === 'purpose' && account.purpose != null) {
                    warnings.push({ kind: 'UNKNOWN_PURPOSE', key: account.purpose });
                }
                unmapped.push(account.accountNumber);
                return;
            }
            const item = resolveMappingWinner(account, candidates, smeItemMap, datevIncomeSides);
            if (item) {
                (item.accounts as unknown[]).push(account);
                return;
            }
            // REV-22122 (R2): a netting group — decided below, once all its accounts are known.
            const netting = nettingGroupOf(candidates, smeItemMap, sideOf);
            if (netting) {
                const group = nettingGroups.get(netting.key) ?? { ...netting, accounts: [] };
                group.accounts.push(account);
                nettingGroups.set(netting.key, group);
                return;
            }
            warnings.push({ kind: 'NO_CANDIDATE', key: account.accountNumber });
            unmapped.push(account.accountNumber);
        };

        if (effectiveReplace) {
            const known = accounts.filter((account) => candidatesFor(account).size > 0);
            AccountMappingFormHelper.removeAccountsFromItems(
                smeMappingItems,
                known.map((account) => account.accountNumber),
            );
            accounts
                .filter((account) => candidatesFor(account).size === 0)
                .forEach((account) => {
                    if (strategy === 'purpose' && account.purpose != null) {
                        warnings.push({ kind: 'UNKNOWN_PURPOSE', key: account.purpose });
                    }
                    // unbekannt für die Vorlage ⇒ bestehende Zuordnung bleibt unangetastet (B4).
                    // Hat das Konto aber KEINE bestehende Zuordnung (neu im Folgejahr), gehört es
                    // in `unmapped` wie im Erstjahr — sonst fällt es lautlos aus der Liste
                    // (Review R2 / C1(y4), AC 7).
                    if (!originallyMappedAccountNumbers.has(account.accountNumber)) {
                        unmapped.push(account.accountNumber);
                    }
                });
            known.forEach(mapAccount);
        } else {
            const unmappedAccounts = AccountMappingFormHelper.getAllUnmappedAccountNumbers(
                accounts,
                originallyMappedAccountNumbers,
            );
            unmappedAccounts.forEach(mapAccount);
        }

        nettingGroups.forEach((group) => {
            const item = resolveNettingGroup(group, smeItemMap);
            group.accounts.forEach((account) => {
                if (item) {
                    (item.accounts as unknown[]).push(account);
                } else {
                    warnings.push({ kind: 'NO_CANDIDATE', key: account.accountNumber });
                    unmapped.push(account.accountNumber);
                }
            });
        });

        return { strategy: mismatch ? 'accountNumber' : strategy, unmapped, warnings: truncateWarningKeys(warnings) };
    }

    /** REV-18627 (B4): löst Konten aus dem Baum, bevor sie neu zugeordnet werden (E6 ersetzen). */
    private static removeAccountsFromItems(
        items: (IAccountMappingTemplateGroupItemFLI | IAccountMappingSubFLI)[],
        accountNumbers: string[],
    ): void {
        if (accountNumbers.length === 0) return;
        const toRemove = new Set(accountNumbers);
        items.forEach((item) => {
            item.accounts = (item.accounts as unknown[]).filter((entry: { type?: string; accountNumber?: string }) => {
                const isStructural = entry?.type === ItemType.OF_THAT_ITEM || entry?.type === ItemType.SUB_FLI;
                if (isStructural) return true;
                return !toRemove.has(entry?.accountNumber);
            }) as never;
        });
    }


    private static getSmeAccountPlan(businessModel: AnnualReportBusinessModel): IAccountMappingMap {
        if (businessModel === AnnualReportBusinessModel.HGB) {
            return germanySmeAccountPlan;
        } else {
            return smeAccountPlan;
        }
    }

    private static getAccountNumberSmeSearchTermToAccounts(
        allAccounts: IAccount[],
        mappedAccountNumbersSet: Set<string>,
        substringCharacterCount: number,
    ): Map<string, IAccount[]> {
        // returns map that has all unmapped account numbers as keys (first 3/4 digits) and corresponding accounts as values
        const accountNumberSmeSearchTermToAccounts: Map<string, IAccount[]> = new Map<string, IAccount[]>();
        // accounts that are not mapped
        allAccounts.forEach((account) => {
            if (mappedAccountNumbersSet.has(account.accountNumber)) {
                return;
            }

            const specifiedDigitsOfAccountNumber = account.accountNumber.substring(0, substringCharacterCount);

            if (!accountNumberSmeSearchTermToAccounts.has(specifiedDigitsOfAccountNumber)) {
                accountNumberSmeSearchTermToAccounts.set(specifiedDigitsOfAccountNumber, []);
            }

            accountNumberSmeSearchTermToAccounts.get(specifiedDigitsOfAccountNumber).push(account);
        });
        return accountNumberSmeSearchTermToAccounts;
    }

    private static getAllUnmappedAccountNumbers(
        allAccounts: IAccount[],
        mappedAccountNumbersSet: Set<string>,
    ): IAccount[] {
        // accounts that are not mapped
        return allAccounts.filter((account) => !mappedAccountNumbersSet.has(account.accountNumber));
    }

    static getMappedAccounts(templateGroup: IAccountMappingTemplateGroup): Set<string> {
        const mappedAccountNumbers: Set<string> = new Set<string>();

        if (templateGroup.isEmpty || templateGroup.isHeader) {
            return mappedAccountNumbers;
        }

        templateGroup.groups
            .filter((group) => !group.isEmpty && !group.isHeader)
            .forEach((group: IAccountMappingTemplateGroup) => {
                const ItemMappedAccounts = this.getItemAccountsNumbers(group.items);
                ItemMappedAccounts.forEach((account) => {
                    mappedAccountNumbers.add(account);
                });
                // call function again for nested group and so on
                if (group.groups != null && group.groups.length > 0) {
                    const groupMappedAccounts = this.getMappedAccounts(group);
                    groupMappedAccounts.forEach((account) => {
                        mappedAccountNumbers.add(account);
                    });
                }
            });
        // Loop on items for current group
        const ItemMappedAccounts = this.getItemAccountsNumbers(templateGroup.items);
        ItemMappedAccounts.forEach((account) => {
            mappedAccountNumbers.add(account);
        });
        return mappedAccountNumbers;
    }

    private static getItemAccountsNumbers(Groupitems: AccountMappingTemplateGroupItem[]): Set<string> {
        let mappedAccountsNumbers: Set<string> = new Set<string>();
        Groupitems.forEach((item) => {
            if (item.type === ItemType.SUB_TOTAL || item.type === ItemType.HEADER || item.type === ItemType.EMPTY) {
                return;
            }

            (item as IAccountMappingTemplateGroupItemFLI).accounts.forEach((account) => {
                if (
                    account.type !== ItemType.SUB_FLI &&
                    account.type !== ItemType.EMPTY &&
                    account.type !== ItemType.HEADER &&
                    account.type !== ItemType.OF_THAT_ITEM
                ) {
                    // getting accounts that are mapped directly to an fli
                    mappedAccountsNumbers.add((account as IAccount).accountNumber);
                } else if (account.type === ItemType.SUB_FLI || account.type === ItemType.OF_THAT_ITEM) {
                    // getting accounts that are mapped under a subfli
                    account.accounts.forEach((account) => mappedAccountsNumbers.add(account.accountNumber));
                }
            });
        });
        return mappedAccountsNumbers;
    }

    private static trimSubstringFromEnd(mainString: string, substringToTrim: string): string {
        /* this check is for stopping a crash happened due to a bug in edit fli implementation that will lead name to be set to undefined*/
        if (mainString === undefined) {
            return EMPTY_STRING;
        }

        if (mainString.endsWith(substringToTrim)) {
            return mainString.slice(0, -substringToTrim.length);
        }
        return mainString;
    }
}

// --- REV-18627 (B1/B7): Regelbau + Kaskade, ein Codepfad für beide Formate. ----------------------

type Sign = 'S' | 'D' | 'H' | null;
type MappingItem = IAccountMappingTemplateGroupItemFLI | IAccountMappingSubFLI;

interface IntervalRule {
    min: number;
    max: number;
    sign: Sign;
    fliName: string;
}

interface EqualityRule {
    sign: Sign;
    fliName: string;
}

/** Mittlerer Delimiter ("-S-"/"-D-"/"-H-") oder trailing Suffix ("-D", Zweck-Format). */
function signOf(value: string): Sign {
    const middle = value.match(/-([SDH])-/);
    if (middle) return middle[1] as 'S' | 'D' | 'H';
    const trailing = value.match(/-([SDH])$/);
    if (trailing) return trailing[1] as 'S' | 'D' | 'H';
    return null;
}

/**
 * Regelbau Nummern-Pfad. Guard im Rückfallzweig (F9): ein Wert, der nicht ausschliesslich aus
 * Ziffern besteht, ist zweck-förmig und wird abgewiesen statt über parseInt fehlinterpretiert.
 */
function buildIntervalRules(plan: IAccountMappingMap, warnings: MappingWarning[]): IntervalRule[] {
    const rangeRegex = /^(\d+)\D+(\d+)$/;
    const delimiterRegex = /[;,|]+/;
    const rules: IntervalRule[] = [];

    Object.keys(plan).forEach((fliName) => {
        const rawAssignments = plan[fliName];
        rawAssignments
            .flatMap((str) => str.split(delimiterRegex))
            .forEach((part) => {
                const cleanStr = part.trim();
                if (!cleanStr) return;

                const rangeMatch = cleanStr.match(rangeRegex);
                if (rangeMatch) {
                    const start = parseInt(rangeMatch[1], 10);
                    const end = parseInt(rangeMatch[2], 10);
                    if (!isNaN(start) && !isNaN(end)) {
                        rules.push({ min: start, max: end, sign: signOf(cleanStr), fliName });
                    }
                    return;
                }
                if (/^\d+$/.test(cleanStr)) {
                    const num = parseInt(cleanStr, 10);
                    if (!isNaN(num)) rules.push({ min: num, max: num, sign: null, fliName });
                    return;
                }
                warnings.push({ kind: 'PURPOSE_SHAPED_VALUE_IN_NUMBER_PATH', key: cleanStr });
            });
    });
    return rules;
}

/** Regelbau Zweck-Pfad: Gleichheit über den refNr (ohne Suffix), als Map<refNr, rules[]>. */
function buildEqualityRules(plan: IAccountMappingMap): Map<string, EqualityRule[]> {
    const map = new Map<string, EqualityRule[]>();
    Object.keys(plan).forEach((fliName) => {
        plan[fliName].forEach((raw) => {
            const sign = signOf(raw);
            const refNr = sign ? raw.replace(/-[SDH]-?$/, '') : raw;
            const list = map.get(refNr) ?? [];
            list.push({ sign, fliName });
            map.set(refNr, list);
        });
    });
    return map;
}

function candidatesForAccountNumberStrategy(account: IAccount, rules: IntervalRule[]): Map<string, Set<Sign>> {
    const candidates = new Map<string, Set<Sign>>();
    const accNum = parseInt(account.accountNumber, 10);
    if (isNaN(accNum)) return candidates;
    rules.forEach((rule) => {
        if (accNum >= rule.min && accNum <= rule.max) {
            if (!candidates.has(rule.fliName)) candidates.set(rule.fliName, new Set());
            candidates.get(rule.fliName)!.add(rule.sign);
        }
    });
    return candidates;
}

function candidatesForPurposeStrategy(
    account: IAccount,
    rulesByRefNr: Map<string, EqualityRule[]>,
): Map<string, Set<Sign>> {
    const candidates = new Map<string, Set<Sign>>();
    if (account.purpose == null) return candidates;
    // Normalisierung (spec-C1 §4.2/M3): Zweck kann auch am KONTO einen S/D/H-Suffix tragen.
    const refNr = account.purpose.replace(/-[SDH]-?$/, '');
    (rulesByRefNr.get(refNr) ?? []).forEach((rule) => {
        if (!candidates.has(rule.fliName)) candidates.set(rule.fliName, new Set());
        candidates.get(rule.fliName)!.add(rule.sign);
    });
    return candidates;
}

/** Priorisiert OF_THAT_ITEM > SUB_FLI > FLI, wie die ALT-Kaskade — jetzt über exakte Namen. */
function resolveWinnerByType(names: string[], smeItemMap: Map<string, MappingItem>): MappingItem | undefined {
    if (names.length === 1) return smeItemMap.get(names[0]);
    const items = names.map((name) => smeItemMap.get(name)).filter((item): item is MappingItem => item != null);
    const ofThat = items.filter((item) => item.type === ItemType.OF_THAT_ITEM);
    if (ofThat.length === 1) return ofThat[0];
    const subFli = items.filter((item) => item.type === ItemType.SUB_FLI);
    if (subFli.length === 1) return subFli[0];
    const fli = items.filter((item) => item.type === ItemType.FLI);
    if (fli.length === 1) return fli[0];
    return undefined;
}

/**
 * Seitenfilter als Mengenfilter (B1): SOLL ⇒ S/D, HABEN ⇒ D/H; leere Menge ⇒ E8-Rückfall.
 * REV-22122 (E1): Soll ist in der Bilanz der positive Saldo, in der GuV einer DATEV-Datei der negative
 * (Erträge positiv, Aufwand negativ) — `datevIncomeSides`. Ohne DATEV-Format bleibt es bei `>= 0`.
 */
function resolveMappingWinner(
    account: IAccount,
    candidates: Map<string, Set<Sign>>,
    smeItemMap: Map<string, MappingItem>,
    datevIncomeSides = false,
): MappingItem | undefined {
    let names = [...candidates.keys()];
    if (names.length > 1) {
        const debit =
            datevIncomeSides && account.type === AccountType.INCOME
                ? account.closingBalance < 0
                : account.closingBalance >= 0;
        const wanted: Sign[] = debit ? ['S', 'D'] : ['D', 'H'];
        const filtered = names.filter((name) => [...(candidates.get(name) ?? [])].some((sign) => wanted.includes(sign)));
        if (filtered.length > 0) names = filtered;
        // sonst E8: ungefilterte Menge (leere gefilterte Menge fällt zurück auf `names`).
    }
    return resolveWinnerByType(names, smeItemMap);
}
// --- REV-22122 (R2): Saldierungsgruppen — `-D` auf BEIDEN Seiten, die Gruppensumme wählt die Seite. -----

type Side = 'assets' | 'liabilities' | 'income' | 'expenses';

interface NettingGroup {
    key: string;
    /** Die Hauptkandidaten (keine davon-Posten) der Gruppe, nach Seite. */
    mainBySide: Map<Side, string[]>;
    /** Bilanz (Aktiva/Passiva) oder Erfolgsrechnung (Ertrag/Aufwand). */
    statement: 'balance' | 'income';
    accounts: IAccount[];
}

const DATEV_SIDES: ReadonlyMap<string, Side> = new Map<string, Side>([
    ['Aktiva', 'assets'],
    ['Passiva', 'liabilities'],
    ['Ertrag', 'income'],
    ['Aufwand', 'expenses'],
]);

/**
 * REV-22122 (SRR F3): the DATEV side of an item, or undefined. A Map, not an object lookup — a stored
 * `accountItemSides` value like `constructor` must not resolve to an inherited property.
 */
function datevSideOf(itemSides: Record<string, string> | undefined, name: string): Side | undefined {
    if (itemSides == null || !Object.prototype.hasOwnProperty.call(itemSides, name)) return undefined;
    return DATEV_SIDES.get(String(itemSides[name]));
}

/**
 * Die Seite jedes Postens: zuerst die DATEV-Saldoart aus der importierten Vorlage, sonst der
 * `fliType`. Nur der oberste Posten trägt `fliType`; seine Unterposten und davon-Posten hängen in
 * seinem `accounts` und erben die Seite, wenn sie keine eigene Saldoart haben.
 */
function sideOfItems(items: MappingItem[], itemSides?: Record<string, string>): Map<string, Side> {
    const sides = new Map<string, Side>();
    const inherit = (item: MappingItem, side: Side): void => {
        sides.set(item.name, datevSideOf(itemSides, item.name) ?? side);
        ((item.accounts ?? []) as unknown as MappingItem[]).forEach((child) => {
            if (child?.type === ItemType.SUB_FLI || child?.type === ItemType.OF_THAT_ITEM) inherit(child, side);
        });
    };
    items.forEach((item) => {
        const fromDatev = datevSideOf(itemSides, item.name);
        const fliType = (item as IAccountMappingTemplateGroupItemFLI).fliType;
        const side = fromDatev ?? (fliType != null ? (String(fliType) as Side) : undefined);
        if (side != null) inherit(item, side);
    });
    return sides;
}

/**
 * Gehört ein Konto, das keinen eindeutigen Posten hat, zu einer Saldierungsgruppe? Ja, wenn seine
 * Hauptkandidaten (davon-Posten zählen nicht, sie sind Anzeige, keine Platzierung) den Schlüssel mit
 * `-D` auf BEIDEN Seiten tragen — Aktiva und Passiva oder Ertrag und Aufwand. So beschreibt DATEV
 * Posten, deren Seite der Saldo der ganzen Gruppe entscheidet: Vor-/Umsatzsteuer (sonstige
 * Vermögensgegenstände oder sonstige Verbindlichkeiten), Deckungsvermögen gegen
 * Pensionsrückstellung (§ 246 Abs. 2 HGB), Saldo Klasse 9, Eigenkapital, Zinsen. Der Schlüssel der
 * Gruppe ist die Menge der Hauptkandidaten — Konten mit derselben Menge werden gemeinsam saldiert.
 */
function nettingGroupOf(
    candidates: Map<string, Set<Sign>>,
    smeItemMap: Map<string, MappingItem>,
    sideOf: Map<string, Side>,
): Omit<NettingGroup, 'accounts'> | undefined {
    const main = [...candidates.keys()].filter((name) => smeItemMap.get(name)?.type !== ItemType.OF_THAT_ITEM);
    const dSides = new Set(main.filter((name) => candidates.get(name)?.has('D')).map((name) => sideOf.get(name)));
    const balance = dSides.has('assets') && dSides.has('liabilities');
    const income = dSides.has('income') && dSides.has('expenses');
    if (!balance && !income) return undefined;
    const mainBySide = new Map<Side, string[]>();
    main.forEach((name) => {
        const side = sideOf.get(name);
        if (side == null) return;
        mainBySide.set(side, [...(mainBySide.get(side) ?? []), name]);
    });
    return { key: [...main].sort().join('\u0001'), mainBySide, statement: balance ? 'balance' : 'income' };
}

/**
 * Die Seite einer Saldierungsgruppe aus dem Vorzeichen ihrer Summe. Die DATEV-Konten tragen in der
 * Bilanz Soll positiv, in der Erfolgsrechnung Ertrag positiv und Aufwand negativ. Eindeutig nur, wenn
 * auf der gewählten Seite genau EIN Hauptposten steht; sonst bleibt die Gruppe ungemappt (nie raten).
 */
function resolveNettingGroup(group: NettingGroup, smeItemMap: Map<string, MappingItem>): MappingItem | undefined {
    const sum = group.accounts.reduce((total, account) => total + (account.closingBalance ?? 0), 0);
    const side: Side =
        group.statement === 'balance' ? (sum >= 0 ? 'assets' : 'liabilities') : sum < 0 ? 'expenses' : 'income';
    const onSide = group.mainBySide.get(side) ?? [];
    return onSide.length === 1 ? smeItemMap.get(onSide[0]) : undefined;
}

/**
 * REV-22122: for DISPLAY only — the captions of the main items (no "davon" rows) an account's key
 * points at in the imported plan, so a `NO_CANDIDATE` notice can say WHAT the user has to decide.
 * Deliberately not part of `MappingWarning`: the result object carries account numbers / purpose keys
 * only, never file content (N4, pinned by C1(x1)/(x2)). The captions shown here are the same ones the
 * mapping tree next to the notice already displays.
 */
export function candidateItemCaptions(
    account: IAccount,
    plan: IAccountMappingMap,
    format: AssignmentsFormat | undefined,
    items: (IAccountMappingTemplateGroupItemFLI | IAccountMappingSubFLI)[],
): string[] {
    const candidates =
        format === 'purpose'
            ? candidatesForPurposeStrategy(account, buildEqualityRules(plan))
            : candidatesForAccountNumberStrategy(account, buildIntervalRules(plan, []));
    const byName = new Map(items.map((item) => [item.name, item] as const));
    const captions = [...candidates.keys()]
        .map((name) => byName.get(name))
        .filter((item): item is MappingItem => item != null && item.type !== ItemType.OF_THAT_ITEM)
        .map((item) => (item.caption ?? item.name).trim());
    // SRR F1: bounded — the captions come from the file and end up in a message.
    return [...new Set(captions)]
        .sort((a, b) => a.localeCompare(b))
        .slice(0, MAX_CANDIDATE_CAPTIONS)
        .map((caption) =>
            caption.length > MAX_CANDIDATE_CAPTION_LENGTH
                ? `${caption.slice(0, MAX_CANDIDATE_CAPTION_LENGTH - 1)}…`
                : caption,
        );
}

/**
 * REV-22122 (SRR F1): a translation with values from the FILE (warning keys, item captions). i18next
 * interpolates its variables recursively — a caption containing `{{candidates}}` expanded itself up to
 * `maxReplaces`, `$t(…)` pulled in other translations, and `skipOnVariables` does not stop the
 * self-expansion in the installed version. So i18next only ever sees fixed markers; the values are
 * spliced in afterwards as plain text (Angular then renders them as a text node).
 */
export function translateWithValues(
    translate: (translationKey: string, options: Record<string, string>) => string,
    translationKey: string,
    values: Record<string, string>,
): string {
    const markers = Object.keys(values).map((name, index) => [name, `\u0000${index}\u0000`] as const);
    const options: Record<string, string> = {};
    markers.forEach(([name, marker]) => (options[name] = marker));
    let text = translate(translationKey, options);
    for (const [name, marker] of markers) text = text.split(marker).join(values[name]);
    return text;
}

/** REV-22122 (SRR F1): at most this many candidate captions per notice entry, each at most this long. */
export const MAX_CANDIDATE_CAPTIONS = 5;
export const MAX_CANDIDATE_CAPTION_LENGTH = 80;

/**
 * REV-22122: a purpose-keyed plan can only place accounts that carry a purpose. An account-number file
 * under a purpose plan (C2(i): purposes stored in year 1, numbers uploaded in year 2) is not a format
 * conflict when nothing is replaced — it simply follows the previous year (AC 6) — so the plan is not
 * handed to the cascade at all, which would otherwise report FORMAT_MISMATCH.
 */
export function planFitsAccounts(plan: IAccountMappingTemplate, accounts: IAccount[] | undefined): boolean {
    if (plan['accountAssignmentsFormat'] !== 'purpose') return true;
    return (accounts ?? []).some((account) => account?.purpose != null);
}

/**
 * REV-22122 (SRR F3): the stored plan is not validated server-side — only an object of string arrays
 * reaches the cascade, anything else means "no fallback" instead of a TypeError in the automatic mapping.
 */
function isAssignmentsMap(value: unknown): boolean {
    if (value == null || typeof value !== 'object' || Array.isArray(value)) return false;
    return Object.values(value as Record<string, unknown>).every(
        (entry) => Array.isArray(entry) && entry.every((item) => typeof item === 'string'),
    );
}

/**
 * REV-22122: the previous year's imported plan the engine may use as its FALLBACK (see
 * `_getWorkingEngineFromData` in account-mapping-form.component.ts), or undefined.
 * - Not after a confirmed taxonomy (`enforceOverwrite`): this year's plan is the one. A cancelled
 *   dialog leaves `enforceOverwrite` false and the delivered taxonomy unused (sec F10) — then the
 *   previous year's plan still applies.
 * - Only a DATEV plan (review R1): the DATEV import stamps `accountAssignmentsFormat`; the aumico-format
 *   import (`createTaxonomyFromAumicoFile`) writes `accountAssignments` without it — those customers
 *   keep the SME plan for accounts new this year.
 * - Only when the plan fits the accounts (`planFitsAccounts`).
 */
export function fallbackPlanFor(
    previousYearImportedTaxonomy: IAccountMappingTemplate | undefined,
    accounts: IAccount[] | undefined,
    enforceOverwrite: boolean,
): IAccountMappingTemplate | undefined {
    if (
        enforceOverwrite ||
        previousYearImportedTaxonomy == null ||
        !isAssignmentsMap(previousYearImportedTaxonomy.accountAssignments)
    ) {
        return undefined;
    }
    const format = previousYearImportedTaxonomy['accountAssignmentsFormat'];
    if (format !== 'accountNumber' && format !== 'purpose') return undefined;
    return planFitsAccounts(previousYearImportedTaxonomy, accounts) ? previousYearImportedTaxonomy : undefined;
}
